import { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { journeyCards } from "@/mocks/home";

gsap.registerPlugin(ScrollTrigger);

export default function JourneyCardsSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const titleRef = useRef<HTMLDivElement>(null);
  const cardsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const section = sectionRef.current;
    const title = titleRef.current;
    const cards = cardsRef.current;
    if (!section || !title || !cards) return;

    const titleLines = title.querySelectorAll("[data-line]");
    const cardItems = cards.querySelectorAll("[data-card]");

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: section,
        start: "top 80%",
        toggleActions: "play none none none",
      },
    });

    tl.fromTo(
      titleLines,
      { y: 50, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.8, stagger: 0.08, ease: "power3.out" }
    );

    tl.fromTo(
      cardItems,
      { y: 80, opacity: 0, scale: 0.95 },
      {
        y: 0,
        opacity: 1,
        scale: 1,
        duration: 0.8,
        stagger: 0.12,
        ease: "power3.out",
      },
      "-=0.4"
    );

    return () => {
      tl.scrollTrigger?.kill();
      tl.kill();
    };
  }, []);

  return (
    <section ref={sectionRef} className="bg-background-50 py-24 md:py-28 lg:py-32 px-6 md:px-10 lg:px-14">
      <div className="max-w-7xl mx-auto">
        <div ref={titleRef} className="mb-12">
          <h2 className="font-heading font-bold uppercase text-[26px] sm:text-[36px] md:text-[52px] lg:text-[72px] text-foreground-950 tracking-tight leading-[0.95]">
            <span data-line className="block overflow-hidden">
              <span className="block">Ihr Weg zur</span>
            </span>
            <span data-line className="block overflow-hidden">
              <span className="block">eigenen Energie</span>
            </span>
          </h2>
        </div>

        <div ref={cardsRef} className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
          {journeyCards.map((card, index) => (
            <a
              key={card.title}
              href={card.href ?? "#"}
              data-card
              className={`group relative overflow-hidden rounded-2xl h-[440px] cursor-pointer block ${
                index === 2 ? "md:col-span-2" : ""
              }`}
            >
              {/* Hintergrundbild immer sichtbar */}
              <img
                src={card.image}
                alt={card.alt ?? card.title}
                loading="lazy"
                decoding="async"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />

              {/* Inhalt: Beschreibung + Label + Pfeil in einer glassy Box unten */}
              <div className="relative z-10 h-full flex flex-col justify-end p-6 md:p-8">
                <div className="bg-white/15 backdrop-blur-md rounded-xl p-5 md:p-6 border border-white/20">
                  <p className="text-white text-sm leading-relaxed mb-4 max-w-lg">
                    {card.description}
                  </p>
                  <div className="flex items-end justify-between">
                    <span className="font-heading font-bold uppercase text-[22px] tracking-tight text-white leading-tight">
                      {card.title}
                    </span>
                    <span className="w-10 h-10 rounded-full bg-accent-500 flex items-center justify-center flex-shrink-0 ml-4">
                      <i className="ri-arrow-right-line text-white w-5 h-5 flex items-center justify-center" />
                    </span>
                  </div>
                </div>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}